/**
 * Type definitions for Countries-States-Cities Integration
 * 
 * IMPORTANT NOTES:
 * - The API returns latitude and longitude as STRINGS, not numbers
 * - These must be parsed to numbers in the APIClient
 * - Example: "latitude": "34.05223" → 34.05223
 */

// ============================================================================
// Core Entity Interfaces
// ============================================================================

/**
 * Basic country information from /countries endpoint
 */
export interface Country {
  id: string;
  name: string;
  nombre_es: string;
  iso2: string;
  iso3: string;
  phoneCode: string;
  capital: string;
  currency: string;
  region: string;
  subregion: string;
}

/**
 * Extended country details from /countries/{iso2} endpoint
 * Includes additional fields like currency symbol, emoji, timezones, etc.
 */
export interface CountryDetails extends Country {
  numericCode: string;
  currencyName: string;
  currencySymbol: string;
  tld: string;
  native: string;
  timezones: string; // JSON string
  translations: string; // JSON string
  latitude: number;
  longitude: number;
  emoji: string;
  emojiU: string;
}

/**
 * Basic state information from /countries/{iso2}/states endpoint
 */
export interface State {
  id: string;
  name: string;
  iso2: string;
}

/**
 * Extended state information from /states endpoint (all states globally)
 * Includes country association, type, and coordinates
 */
export interface StateWithCountry extends State {
  countryId: string;
  countryCode: string;
  countryName?: string;
  type: string; // e.g., "state", "province", "region", "territory"
  latitude: number;
  longitude: number;
}

/**
 * State details from /countries/{countryIso2}/states/{stateIso2} endpoint
 * Same structure as StateWithCountry
 */
export type StateDetails = StateWithCountry;

/**
 * City information from /countries/{countryIso2}/states/{stateIso2}/cities endpoint
 * IMPORTANT: Includes coordinates (parsed from strings to numbers)
 * 
 * Note: stateId, stateCode, countryId, countryCode are inferred from the request context
 * as they are not included in the API response
 */
export interface City {
  id: string;
  name: string;
  stateId: string;
  stateCode: string;
  countryId: string;
  countryCode: string;
  latitude?: number; // Parsed from string
  longitude?: number; // Parsed from string
}

/**
 * Basic city information from /countries/{iso2}/cities endpoint
 * Includes state association for auto-completing state on city selection
 */
export interface CityBasic {
  id: string;
  name: string;
  stateCode?: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
}

// ============================================================================
// Configuration Interfaces
// ============================================================================

/**
 * Configuration for APIClient
 */
export interface APIClientConfig {
  apiKey: string;
  baseURL: string;
  timeout: number;
  maxRetries: number;
  retryDelay: number;
}

/**
 * Configuration for CacheManager
 */
export interface CacheConfig {
  defaultTTL: number;
  maxSize: number;
}

/**
 * Configuration for LocationService
 */
export interface LocationServiceConfig {
  apiClient: APIClient;
  cacheManager: CacheManager;
  ttl: CacheTTL;
}

/**
 * Cache TTL configuration (in milliseconds)
 * Based on official API documentation recommendations
 */
export interface CacheTTL {
  countries: number; // 24-48 hours (very stable data)
  states: number; // 12-24 hours (occasional updates)
  cities: number; // 6-12 hours (more frequent updates)
}

// ============================================================================
// API Response Interfaces
// ============================================================================

/**
 * Generic API response wrapper
 */
export interface APIResponse<T> {
  data: T;
  status: number;
  headers: Record<string, string>;
}

/**
 * Cache entry structure
 */
export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  expiresAt: number;
}

// ============================================================================
// UI State Interfaces
// ============================================================================

/**
 * Loading state for different data types
 */
export interface LoadingState {
  countries?: boolean;
  states?: boolean;
  cities?: boolean;
}

/**
 * Location selection state for LocationSelector component
 */
export interface LocationSelection {
  country: {
    id: string;
    name: string;
    iso2: string;
  } | null;
  state: {
    id: string;
    name: string;
    iso2: string;
  } | null;
  city: {
    id: string;
    name: string;
    latitude?: number;
    longitude?: number;
  } | null;
}

// ============================================================================
// Validation Interfaces
// ============================================================================

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

/**
 * Validation error details
 */
export interface ValidationError {
  field: 'country' | 'state' | 'city';
  message: string;
  code: string;
}

// ============================================================================
// Component Props Interfaces
// ============================================================================

/**
 * Props for LocationSelector component
 */
export interface LocationSelectorProps {
  // Values
  value?: LocationSelection;
  onChange: (location: LocationSelection) => void;

  // Configuration
  required?: boolean;
  disabled?: boolean;
  levels?: ('country' | 'state' | 'city')[];

  // Customization
  labels?: {
    country?: string;
    state?: string;
    city?: string;
  };
  placeholders?: {
    country?: string;
    state?: string;
    city?: string;
  };

  // Validation
  error?: string;
  onValidate?: (location: LocationSelection) => string | null;
}

// ============================================================================
// Error Classes
// ============================================================================

/**
 * Configuration error (e.g., missing API key)
 */
export class ConfigurationError extends Error {
  constructor(message: string, public field: string) {
    super(message);
    this.name = 'ConfigurationError';
  }
}

/**
 * API error with status code and endpoint information
 */
export class APIError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public endpoint: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'APIError';
  }
}

/**
 * Cache error
 */
export class CacheError extends Error {
  constructor(message: string, public key: string) {
    super(message);
    this.name = 'CacheError';
  }
}

/**
 * Location validation error
 */
export class LocationValidationError extends Error {
  constructor(
    message: string,
    public field: string,
    public code: string
  ) {
    super(message);
    this.name = 'LocationValidationError';
  }
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Helper function to check if object has basic location properties
 */
function hasBasicLocationProps(obj: unknown): boolean {
  if (typeof obj !== 'object' || obj === null) return false;
  const o = obj as Record<string, unknown>;
  return typeof o['id'] === 'string' && typeof o['name'] === 'string' && typeof o['iso2'] === 'string';
}

/**
 * Type guard for Country
 */
export function isCountry(obj: unknown): obj is Country {
  return hasBasicLocationProps(obj);
}

/**
 * Type guard for State
 */
export function isState(obj: unknown): obj is State {
  return hasBasicLocationProps(obj);
}

/**
 * Type guard for City
 */
export function isCity(obj: unknown): obj is City {
  if (typeof obj !== 'object' || obj === null) return false;
  const o = obj as Record<string, unknown>;
  return typeof o['id'] === 'string' && typeof o['name'] === 'string';
}

// ============================================================================
// Forward Declarations (to avoid circular dependencies)
// ============================================================================

// These will be implemented in their respective files
export interface APIClient {
  fetchCountries(): Promise<Country[]>;
  fetchCountryDetails(iso2: string): Promise<CountryDetails>;
  fetchStates(countryIso2: string): Promise<State[]>;
  fetchAllStates(query?: string): Promise<StateWithCountry[]>;
  fetchStateDetails(countryIso2: string, stateIso2: string): Promise<StateDetails>;
  fetchCities(countryIso2: string, stateIso2: string): Promise<City[]>;
  fetchCitiesByCountry(countryIso2: string): Promise<CityBasic[]>;
}

export interface CacheManager {
  get<T>(key: string): T | null;
  set<T>(key: string, data: T, ttl?: number): void;
  has(key: string): boolean;
  invalidate(key: string): void;
  invalidatePattern(pattern: RegExp): void;
  clear(): void;
}
